import React from "react";

import styles from "../../../../styles/atenaai-payment.module.scss";

import { Loading } from "../../../components/Loading";
import { Drawer } from "../../../components/Drawer";
import { Button } from "../../../components/Button";
import { Context } from "pages/Integrations";
import { Message, Select, Tabs } from "@components";
import { Method, PaymentMethod } from "shared/utils/Payment";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import { IntegrationsAPI } from "providers/IntegrationsAPI";

const PaymentTabs = {
  ONLINE: {
    key: "ONLINE",
    name: "Pagamento online",
  },
  OFFLINE: {
    key: "OFFLINE",
    name: "Pagamento na entrega",
  },
};

export default class BlingPaymentDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingPage: true,
      loading: false,
      activeTab: PaymentTabs.ONLINE.key,
    };
  }

  componentDidMount() {
    this.loadConfigs();
  }

  async loadConfigs() {
    try {
      this.setState({ loadingPage: true });

      const environmentId = EnvironmentStorage.getEnvironment().id;

      const methodsResponse = await IntegrationsAPI.getBlingPaymentMethods(
        environmentId
      );
      const methods = methodsResponse.getData([]);

      const configurationsResponse =
        await IntegrationsAPI.getBlingConfigurations(environmentId);
      const configurations = configurationsResponse.getData([]);

      this.setState({
        loadingPage: false,
        payments: methods,
        groups: this.buildGroups(),
        methods: configurations.paymentMethods || { ONLINE: {}, OFFLINE: {} },
      });
    } catch (err) {
      console.log(err);
    }
  }

  componentDidUpdate({ methods }) {
    const prevMethods = JSON.stringify(methods || {});
    const currentMethods = JSON.stringify(this.props.methods || {});
    if (this.props.methods && prevMethods !== currentMethods) {
      this.setState({
        methods: {
          ONLINE: this.props.methods.ONLINE || {},
          OFFLINE: this.props.methods.OFFLINE || {},
        },
      });
    }
  }

  async onSave() {
    try {
      const { onSave } = this.props;
      const { methods } = this.state;

      this.setState({ loading: true });

      const environmentId = EnvironmentStorage.getEnvironment().id;

      const configurationsResponse = await IntegrationsAPI.getBlingConfigurations(environmentId);
      const currentConfigurations = configurationsResponse.getData({});

      const newConfigurations = {
        ...currentConfigurations,
        paymentMethods: methods,
      }

      const updateResponse = await IntegrationsAPI.upsertBlingConfigurations(environmentId, newConfigurations);

      this.setState({ loading: false });

      if (updateResponse.getErrors([]).length === 0) {
        onSave && onSave();
      } else {
        alert({
          text: `Não foi possível alterar a configuração: ${
            updateResponse.getErrors()[0]
          }`,
        });
      }
    } catch (e) {
      this.setState({ loading: false });
    }
  }

  buildGroups() {
    const groups = {
      CREDIT: {
        name: "Crédito",
        type: PaymentMethod.CREDIT,
        items: [],
      },
      DEBIT: {
        name: "Débito",
        type: PaymentMethod.DEBIT,
        items: [],
      },
      FOOD: {
        name: "Alimentação",
        type: PaymentMethod.FOOD,
        items: [],
      },
      MEAL: {
        name: "Refeição",
        type: PaymentMethod.MEAL,
        items: [],
      },
      MONEY: {
        name: "Dinheiro",
        type: PaymentMethod.MONEY,
        items: [],
      },
    };

    for (const method of Object.values(Method)) {
      if ((method.type & PaymentMethod.CREDIT) >= PaymentMethod.CREDIT) {
        groups.CREDIT.items.push(method);
      }

      if ((method.type & PaymentMethod.DEBIT) >= PaymentMethod.DEBIT) {
        groups.DEBIT.items.push(method);
      }

      if ((method.type & PaymentMethod.MEAL) >= PaymentMethod.MEAL) {
        groups.MEAL.items.push(method);
      }

      if ((method.type & PaymentMethod.FOOD) >= PaymentMethod.FOOD) {
        groups.FOOD.items.push(method);
      }

      if ((method.type & PaymentMethod.MONEY) >= PaymentMethod.MONEY) {
        groups.MONEY.items.push(method);
      }
    }

    const sortedGroups = Object.values(groups).map((group) => {
      group.items = group.items.sort((prev, next) => {
        return next.importance - prev.importance;
      });

      return group;
    });

    return sortedGroups;
  }

  buildSelectOptions(options, selectedValue) {
    const builtOptions = options.map((option) => ({
      label: option.name,
      value: option.id,
      selected: option.id === selectedValue,
    }));

    return [
      {
        label: "Selecione",
        value: null,
        selected: !selectedValue,
      },
      ...builtOptions,
    ];
  }

  render() {
    const { loading, loadingPage, activeTab, groups, methods, payments } = this.state;
    const { visible, onClose } = this.props;

    return (
      <Drawer
        visible={visible}
        onClose={onClose}
        alreadyDrawer
        title={"Bling - Formas de pagamento"}
        footer={
          <Button
            design="primary"
            className="w-100"
            onClick={() => this.onSave()}
            loading={loading}
            disabled={loading}
          >
            Confirmar
          </Button>
        }
      >
        <Context />

        {loadingPage ? (
          <div className="d-flex justify-center align-center h-100">
            <Loading fullScreen />
          </div>
        ) : (
          <div className={styles.scrollContainer}>
            <Message
              type="warning"
              title="Configuração de pagamento"
              text="Não é necessário configurar todas as formas de pagamento. Configure apenas aquelas que serão utilizadas pelo PDV."
              className="mb-20"
            />

            <Tabs
              tabs={Object.values(PaymentTabs)}
              activeTab={activeTab}
              onTabSelected={(selectedTab) => {
                this.setState({ activeTab: selectedTab });
              }}
            />

            <div className={styles.paymentsMethodsContainer}>
              {groups.map((group, _i) => {
                const key = `${_i}_${activeTab}`;
                return (
                  <div className={styles.paymentsGroups} key={key}>
                    <p className={styles.paymentMethodName}>{group.name}</p>
                    <div className={styles.methods}>
                      {group.items.map((method, index) => {
                        const paymentId =
                          methods?.[activeTab]?.[group.type]?.[method.alias];

                        return (
                          <div className={styles.method} key={index}>
                            <div className={styles.methodDetail}>
                              <img
                                src={`${process.env.REACT_APP_ASSETS_URL}/cards/${method.alias}.png`}
                                onError={({ currentTarget }) => {
                                  currentTarget.onerror = null; // prevents looping
                                  currentTarget.src = `${process.env.REACT_APP_ASSETS_URL}/cards/DEFAULT.png`;
                                }}
                                alt={method.alias}
                                className={styles.flagImage}
                              />

                              <label className={styles.methodName}>
                                {method.name}
                              </label>
                            </div>

                            <Select
                              design="two"
                              onChange={(e) =>
                                this.setState((prevState) => {
                                  return {
                                    ...prevState,
                                    methods: {
                                      ...prevState.methods,
                                      [activeTab]: {
                                        ...prevState.methods[activeTab],
                                        [group.type]: {
                                          ...prevState.methods[activeTab][
                                            group.type
                                          ],
                                          [method.alias]: e.target.value,
                                        },
                                      },
                                    },
                                  };
                                })
                              }
                              value={paymentId}
                              options={this.buildSelectOptions(
                                payments,
                                paymentId
                              )}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Drawer>
    );
  }
}
